<template>
  <div class="pt-wrap">
    <!-- Scroll-left affordance. Shown only when there's content hidden to the
         left AND the pointer is a mouse (touch users swipe). -->
    <button
      v-show="canScrollLeft"
      type="button"
      class="pt-arrow pt-arrow--left"
      aria-label="Scroll tabs left"
      tabindex="-1"
      @click="scrollByStep(-1)"
    >
      <i class="fa-solid fa-chevron-left"></i>
    </button>

    <div
      ref="scroller"
      class="pt"
      role="tablist"
      @scroll.passive="updateEdges"
    >
      <button
        v-for="tab in tabs"
        :key="tab.value"
        type="button"
        role="tab"
        class="pt__tab"
        :class="{ 'pt__tab--active': tab.value === value }"
        :aria-selected="tab.value === value"
        @click="$emit('input', tab.value)"
      >
        <span class="pt__label">{{ tab.label }}</span>
        <span
          v-if="tab.count !== undefined && tab.count !== null"
          class="pt__count"
          :class="{ 'pt__count--active': tab.value === value }"
        >
          {{ tab.count }}
        </span>
      </button>
    </div>

    <button
      v-show="canScrollRight"
      type="button"
      class="pt-arrow pt-arrow--right"
      aria-label="Scroll tabs right"
      tabindex="-1"
      @click="scrollByStep(1)"
    >
      <i class="fa-solid fa-chevron-right"></i>
    </button>

    <!-- Edge fades — the primary "there's more" cue on every device. Purely
         decorative + click-through (pointer-events: none). -->
    <div class="pt-fade pt-fade--left" :class="{ 'pt-fade--on': canScrollLeft }" aria-hidden="true"></div>
    <div class="pt-fade pt-fade--right" :class="{ 'pt-fade--on': canScrollRight }" aria-hidden="true"></div>
  </div>
</template>

<script>
/**
 * Underlined-tab navigation with optional count badges.
 *
 * Shows item count in each tab so users know scope before clicking — the
 * pattern used by Linear, GitHub, Notion, Stripe Dashboard. Active count
 * inverts to white-on-blue for visual harmony with the underline.
 *
 * The strip scrolls horizontally when the tabs don't fit (mobile + the
 * ~900–1200px tablet range). Bare overflow gave no hint that more tabs
 * existed, so we add: edge fade gradients (all devices), scroll arrows
 * (mouse only — touch users swipe), and auto-scrolling the active tab into
 * view. Edge state is recomputed on scroll, resize, and tab-set changes.
 */
export default {
  props: {
    tabs: {
      type: Array,
      required: true,
    },
    value: {
      type: String,
      default: '',
    },
  },

  emits: ['input'],

  data() {
    return {
      canScrollLeft: false,
      canScrollRight: false,
    }
  },

  watch: {
    // New active tab may be off-screen (e.g. selected programmatically) —
    // bring it into view, then refresh the edge cues.
    value() {
      this.$nextTick(() => {
        this.scrollActiveIntoView()
        this.updateEdges()
      })
    },

    // Tab set / counts changed → widths shifted, recompute.
    tabs() {
      this.$nextTick(this.updateEdges)
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.updateEdges()
      this.scrollActiveIntoView()
    })

    // Recompute on container resize (responsive breakpoints, font load) and
    // on window resize as a fallback for environments without ResizeObserver.
    if (typeof ResizeObserver !== 'undefined') {
      this._ro = new ResizeObserver(() => this.updateEdges())
      if (this.$refs.scroller) this._ro.observe(this.$refs.scroller)
    }
    window.addEventListener('resize', this.updateEdges, { passive: true })
  },

  beforeDestroy() {
    if (this._ro) {
      this._ro.disconnect()
      this._ro = null
    }
    window.removeEventListener('resize', this.updateEdges)
  },

  methods: {
    updateEdges() {
      const el = this.$refs.scroller
      if (!el) return
      // 1px slack absorbs sub-pixel rounding so the right fade reliably
      // clears once fully scrolled.
      this.canScrollLeft = el.scrollLeft > 1
      this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1
    },

    scrollByStep(dir) {
      const el = this.$refs.scroller
      if (!el) return
      el.scrollBy({ left: dir * el.clientWidth * 0.6, behavior: 'smooth' })
    },

    scrollActiveIntoView() {
      const el = this.$refs.scroller
      if (!el) return
      const active = el.querySelector('.pt__tab--active')
      if (active && active.scrollIntoView) {
        active.scrollIntoView({ block: 'nearest', inline: 'nearest' })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Self-contained tokens — keep the component portable across bundles (mirrors
// the admin cool-slate palette in admin-dashboard/helpers/_theme.scss; this
// file lives in the shared bundle, so it must not reach into a module's theme).
$pt-border:       rgba(0, 0, 0, 0.08);
$pt-text:         #2b3a4a;
$pt-text-muted:   #6c7a89;
$pt-blue:         #1f73c1;
$pt-blue-soft:    #e9f1fa;
$pt-bg-surface:   #ffffff;
$pt-bg-soft:      #f6f8fa;
$pt-radius-md:    8px;
$pt-radius-pill:  999px;
$pt-duration:     .18s;
$pt-duration-lg:  .26s;
$pt-ease:         cubic-bezier(.4, 0, .2, 1);
$pt-focus-ring:   0 0 0 3px rgba(67, 146, 212, 0.18);
$pt-bp-md:        768px;

// Positioning context for the absolutely-placed arrows + fades.
.pt-wrap {
  position: relative;
  margin-bottom: 1.5rem;

  // The app shell (#cl-wrapper) is a `display: table` with auto layout, which
  // sizes its cell to content max-content. `overflow-x: auto` on `.pt` only
  // zeroes min-content, so the tabs' full width still leaked up and widened
  // the cell past the viewport (horizontal page scroll on tablet/mobile).
  // `contain: inline-size` computes this box's width independent of its
  // contents, so the scrolling tab strip can no longer push ancestors wider.
  contain: inline-size;
}

.pt {
  display: flex;
  align-items: stretch;
  gap: 2px;
  position: relative;

  // Horizontal scroll fallback for every viewport — 8 tabs overflow before
  // hitting the mobile breakpoint on tablet/small-laptop widths
  // (≈900–1200px), so the "wait for mobile bp" gate was cutting off the
  // last tab on intermediate sizes (the "BILL..." truncation). Scroll is
  // always-on now; the scrollbar itself stays hidden so the bar reads as
  // a clean strip, swipe / wheel discovers the rest.
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }

  // Bottom divider — moved from a `::after` absolute pseudo to a real
  // `border-bottom`. Pseudo-elements with `position: absolute` only span
  // the viewport-width on a horizontally-scrolling container, leaving the
  // overflowed area underlined-less. A border on the container ALWAYS
  // sits beneath the visible row — active tab's blue underline still
  // overlays it via its own taller `::after`.
  border-bottom: 1px solid $pt-border;

  &__tab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px 14px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .05em;
    color: $pt-text-muted;
    cursor: pointer;
    border: none;
    background: none;
    font-family: inherit;
    flex-shrink: 0;
    scroll-snap-align: start;
    white-space: nowrap;
    position: relative;
    border-radius: $pt-radius-md $pt-radius-md 0 0;
    transition: color $pt-duration $pt-ease, background $pt-duration $pt-ease;
    z-index: 1;

    // Animated underline indicator — overlays the container's border-bottom.
    // `bottom: -1px` pulls the underline down by 1px so the active tab's
    // blue strip merges with the gray strip-divider line, hiding it under
    // the underline (single visual line, not two stacked).
    &::after {
      content: '';
      position: absolute;
      left: 12px;
      right: 12px;
      bottom: -1px;
      height: 2px;
      background: $pt-blue;
      border-radius: 2px 2px 0 0;
      transform: scaleX(0);
      transform-origin: center;
      transition: transform $pt-duration-lg $pt-ease;
    }

    &:focus-visible {
      outline: none;
      box-shadow: $pt-focus-ring;
    }

    &:hover:not(&--active) {
      color: $pt-text;
      background: rgba(24, 95, 165, 0.04);
    }

    &--active {
      color: $pt-blue;
      font-weight: 700;

      &::after {
        transform: scaleX(1);
      }
    }

    @media (max-width: $pt-bp-md) {
      min-height: 44px;
      padding: 13px 14px 15px;
    }
  }

  &__label {
    line-height: 1;
  }

  &__count {
    font-size: 11px;
    font-weight: 600;
    padding: 1px 7px;
    border-radius: $pt-radius-pill;
    background: $pt-bg-soft;
    color: $pt-text-muted;
    line-height: 1.4;
    transition: background $pt-duration $pt-ease, color $pt-duration $pt-ease;
    min-width: 22px;
    text-align: center;

    &--active {
      background: $pt-blue-soft;
      color: $pt-blue;
    }
  }
}

// ── Edge fades ──
// Gradient masks that signal "more tabs this way". Sit above the strip but
// below the 1px divider line (bottom: 1px) so the underline stays crisp.
// `pointer-events: none` keeps the tabs underneath fully clickable.
.pt-fade {
  position: absolute;
  top: 0;
  bottom: 1px;
  width: 40px;
  pointer-events: none;
  opacity: 0;
  transition: opacity $pt-duration $pt-ease;
  z-index: 2;

  &--on { opacity: 1; }

  &--left {
    left: 0;
    background: linear-gradient(to right, $pt-bg-surface 25%, rgba(255, 255, 255, 0));
  }

  &--right {
    right: 0;
    background: linear-gradient(to left, $pt-bg-surface 25%, rgba(255, 255, 255, 0));
  }
}

// ── Scroll arrows ──
// Shown on every device (incl. touch) whenever there's content to scroll
// toward — `v-show` toggles them per direction. The arrow sits on a solid
// surface chip above the fade so the chevron stays legible over the tabs.
.pt-arrow {
  display: inline-flex;
  position: absolute;
  top: 0;
  bottom: 1px;
  z-index: 3;
  width: 30px;
  align-items: center;
  justify-content: center;
  border: none;
  background: $pt-bg-surface;
  color: $pt-text-muted;
  cursor: pointer;
  padding: 0;
  transition: color $pt-duration $pt-ease;

  &:hover { color: $pt-blue; }
  &:active { color: $pt-blue; }

  &--left  { left: 0; }
  &--right { right: 0; }

  i { font-size: 12px; }

  // Slightly larger tap target on touch viewports.
  @media (max-width: $pt-bp-md) {
    width: 34px;
    i { font-size: 13px; }
  }
}
</style>
